import type { LoaderFunctionArgs } from "@remix-run/node";
import { json } from "@remix-run/node";
import { Link, Outlet, useLoaderData } from "@remix-run/react";

import { TopbarMenu } from "~/components/layout";
import type {
  GetProfileQuery,
  Permission,
} from "~/graphql/__generated__/types";
import { getProfile } from "~/graphql/queries/profile";
import { requireAuth } from "~/lib/auth";
import { graphQLFetch } from "~/lib/graphql-fetcher";
import { getSession, USER_BRANCH } from "~/session.server";

type LoaderData =
  | {
      isAuthenticated: false;
    }
  | {
      isAuthenticated: true;
      profile: NonNullable<GetProfileQuery["me"]>;
      permissions: Permission[];
      activeBranch: string | null;
      notificationCount: number;
    };

export const loader = async ({ request }: LoaderFunctionArgs) => {
  const accessToken = await requireAuth(request);

  if (!accessToken) {
    return json<LoaderData>({ isAuthenticated: false });
  }

  const session = await getSession(request);

  const response = await graphQLFetch<GetProfileQuery>({
    query: getProfile,
    request,
    session,
  });

  const profile = response.data?.me ?? null;

  if (!profile) {
    return json<LoaderData>({ isAuthenticated: false });
  }

  return json<LoaderData>({
    isAuthenticated: true,
    profile,
    permissions: (profile.peran?.permises as Permission[]) || [],
    activeBranch: session.get(USER_BRANCH) ?? null,
    notificationCount: 0,
  });
};

export default function DocsLayoutPage() {
  const data = useLoaderData<typeof loader>();

  if (!data.isAuthenticated) {
    return (
      <div className="flex min-h-screen flex-col gap-5 bg-gray-300">
        <header className="flex h-16 w-full items-center justify-between bg-[#042250] px-8 shadow-lg">
          <Link
            to="/"
            className="group relative flex items-center gap-4 overflow-hidden rounded-2xl px-4 py-2 shadow-sm ring-1 ring-white/40"
            aria-label="Kembali ke beranda"
          >
            <span
              aria-hidden="true"
              className="absolute inset-0 bg-overlay-onboard-1 bg-cover bg-center"
            />
            <span
              aria-hidden="true"
              className="absolute inset-0 bg-white/85 transition-colors duration-200 group-hover:bg-white/90"
            />
            <span className="relative z-10 flex items-center gap-4">
              <img
                src="/logo/logo-cirql.svg"
                alt="Trustmedis Logo"
                className="h-8 w-auto drop-shadow-sm"
              />
              <span className="hidden h-6 w-px bg-slate-200/80 sm:block" />
              <img
                src="/logo/logo-ihc.svg"
                alt="IHC Logo"
                className="h-6 w-auto drop-shadow-sm"
              />
            </span>
          </Link>
        </header>
        <Outlet />
      </div>
    );
  }

  const { profile, permissions, activeBranch, notificationCount } = data;
  const menuItems = (permissions ?? []).filter(Boolean) as Permission[];

  return (
    <div className="flex min-h-screen flex-col gap-5 bg-gray-300">
      <TopbarMenu
        notificationCount={notificationCount}
        menu={menuItems}
        activeBranch={activeBranch ?? ""}
        user={profile}
      />
      <main className="px-4 pb-16 sm:px-6 lg:px-10">
        <Outlet />
      </main>
    </div>
  );
}
